import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useCachedFetch } from '@/composables/useFetch';
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'

export let useStormStore = defineStore('stormStore', () => {

  // Storm type codes
  // XX (unknown)
  // LO (low or remnants)
  // SD (subtrop depression)
  // SS (subtrop storm)
  // PT (post-trop)
  // TD (tropical depression)
  // TS (tropical storm)
  // HU (hurricane)

  const { $STORM_TRACK_DATA_URL, $STORM_TRACK_DATA_FILE } = useConfigurationGlobals();

  const stormsData = ref({});
  const stormAdvisoryText = ref(null);
  const stormForecastContent = ref(null);
  const stormForecastDoc = ref(null);
  const pointForecastContent = ref(null);

  const selectedStorm = ref(null);

  const activeStorms = computed(() => {
    const stormsKeyValues = Object.entries(stormsData.value);
    const storms = [];

    stormsKeyValues.forEach(storm => {
      if (storm[1].active) storms.push({...stormsData.value[storm[0]]});
    })

    return storms;
  });

  const activeAtlanticStorms = computed(() => activeStorms.value.filter(storm => storm.stormregion === 'al'));
  const activeEasternPacificStorms = computed(() => activeStorms.value.filter(storm => storm.stormregion === 'ep' || storm.stormregion === 'cp'));
  const hasActiveStorms = computed(() => activeStorms.value.length > 0);
  const stormForecastURL = computed(() => selectedStorm.value?.forecast.forecastkml);
  const stormAdvisoryURL = computed(() => selectedStorm.value?.advisory.advisorytxt);

  const stormCategory = computed(() => {
    return (stormId) => {
      const storm = getStorm(stormId);

      if (storm.status.type.toLowerCase() !== 'hu') return false;

      const stormWindSpeed = storm.status.windsustained_kts;

      return stormWindSpeed >= 64 && stormWindSpeed <= 82
        ? 1
        : stormWindSpeed >= 83 && stormWindSpeed <= 95
        ? 2
        : stormWindSpeed >= 96 && stormWindSpeed <= 112
        ? 3
        : stormWindSpeed >= 113 && stormWindSpeed <= 136
        ? 4
        : stormWindSpeed >= 137
        ? 5
        : false;
    }
  })

  async function getStormsData() {
    const url = `${$STORM_TRACK_DATA_URL}${$STORM_TRACK_DATA_FILE}`

    try {
      const { error, data, isLoading } = await useCachedFetch(url, {}, false)

      if (error.value) {
        throw error.value
      }

      // Remove the jsonp padding callback function
      const jsonText = data.value.match(/storms_callback\((.*)\)/s)

      stormsData.value = JSON.parse(jsonText[1])

      const stormIds = Object.keys(stormsData.value);

      stormIds.forEach((stormId) => {
        const storm = stormsData.value[stormId]
        stormsData.value[stormId].id = `${storm.stormregion}${storm.stormnumber}${storm.stormyear}`
      });

      if (activeStorms.value.length === 0) return stormsData.value;
      
      setDefaultSelectedStorm()
      getStormPointForecastContent()
      getStormAdvisoryText()

      return stormsData.value
    } catch (error) {
      console.warn(error)

      return error
    }
  }

  async function getStormPointForecastContent(url = stormForecastURL.value) {
    if (!url) return;

    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    const apiUrl = `${$STORM_TRACK_DATA_URL}${fileName}`

    try {
      const { error, data, isLoading } = await useCachedFetch(apiUrl, {}, false)

      if (error.value) {
        throw error.value
      }

      stormForecastDoc.value = parseStormForecastKML(data.value);

      return stormForecastContent.value
    } catch (error) {
      console.warn(error)

      return error
    }
  }

  async function getStormAdvisoryText(url = stormAdvisoryURL.value) {
    if (!url) return;

    const urlParts = url.split('/')
    const fileName = urlParts[urlParts.length - 1]
    const apiUrl = `${$STORM_TRACK_DATA_URL}${fileName}`

    try {
      const { error, data, isLoading } = await useCachedFetch(apiUrl, {}, false)

      if (error.value) {
        throw error.value
      }

      stormAdvisoryText.value = data.value

      return data.value
    } catch (error) {
      console.warn(error)

      return error
    }
  }

  function parseStormForecastKML(data) {
      const parser = new DOMParser();

      return parser.parseFromString(data, 'application/xml')
  }

  function convertPointForecastToObject(pointId) {
    const primaryDataLabels = ['storm name', 'advisory', 'title', 'valid at'];
    const placemark = stormForecastDoc.value.getElementById(pointId)
    const data = [...placemark.querySelectorAll('Data')];

    if (data.length > 0);

    const pointForecastDataObject = {};
    pointForecastDataObject.data = {}

    data.forEach(d => {
      const name = d.getAttribute('name');
      const key = name.toLowerCase().split(' ').join('-');
      const value = d.querySelector('value').innerHTML;

      if (primaryDataLabels.includes(name.toLowerCase())) {
        pointForecastDataObject[key] = value

        return;
      }
      
      pointForecastDataObject.data[key] = {
        label: name,
        value
      }
    })

    pointForecastContent.value = pointForecastDataObject;
  }

  function setDefaultSelectedStorm() {
    if (selectedStorm.value) return;

    selectedStorm.value = activeStorms.value.length > 0 ? activeStorms.value[0] : null;
  }

  function setSelectedStorm(stormId) {
    selectedStorm.value = getStorm(stormId);

    getStormPointForecastContent();
    getStormAdvisoryText();
  }

  function getStorm(stormId) {
    return activeStorms.value.filter(storm => storm.id === stormId)[0];
  }

  function updatePointForecastContent(content) {
    pointForecastContent.value = content;
  }

  return {
    stormsData,
    stormAdvisoryText,
    stormForecastContent,
    pointForecastContent,
    selectedStorm,
    activeStorms,
    activeAtlanticStorms,
    activeEasternPacificStorms,
    hasActiveStorms,
    stormForecastURL,
    stormAdvisoryURL,
    stormCategory,
    updatePointForecastContent,
    convertPointForecastToObject,
    getStormsData,
    getStormPointForecastContent,
    getStormAdvisoryText,
    setSelectedStorm
  }
})
